import { RecordStates } from '../constants/ReduxConstants';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import dataModel from '../models/DataModel';

/**
 * Superclass for all DAO objects
 */
export default class DAO {

  constructor(store, itemType) {
    this.store = store;
    this.itemType = itemType;

    this.pendingRequests = new Map();
    this.mapToReturn = this.mapToReturn.bind(this);
    this.markDirty = this.markDirty.bind(this);
  }

  get stateTree() {
    return this.store.getState();
  }

  setStore(newStore) {
    this.store = newStore;
  }

  findItem(id, type) {
    //allow for possibility of finding a different type of object in store
    type = type ? type : this.itemType;
    return StoreItemProcessing.findItem(type, id);
  }

  addNormalizeHook(key, hook) {
    StoreItemProcessing.addNormalizeHook(key, hook);
  }

  //Modified params to pass in options and therefore SOURCEHASPRIORITY
  normalizeAndSyncToStore(data, options) {
    return StoreItemProcessing.normalizeAndSyncToStore(this.itemType, data, options);
  }

  normalizeAndSyncCollectionToStore(data, options) {
    return StoreItemProcessing.normalizeAndSyncCollectionToStore(this.itemType, data, options);
  }

  normalizeItem(item, options) {
    return StoreItemProcessing.normalizeItem(this.itemType, item, options);
  }

  setItemInStore(item) {
    return StoreItemProcessing.setItemInStore(this.itemType, item);
  }

  clearMyState(objects, toIgnore, unsetFunction, clearFunction, options) {
    if (toIgnore && toIgnore.length > 0) {
      for (const key of Object.keys(objects)) {
        if (!this.objectIsIgnored(toIgnore, key, objects)) {
          if (unsetFunction) {
            unsetFunction(key);
          }
        }
        else if (!options.ignoreExpired && this.objectHasExpired(objects[key], options)) {
          if (unsetFunction) {
            unsetFunction(key);
          }
        }
      }
    }
    else if (clearFunction) {
      clearFunction();
    }
  }

  objectIsIgnored(toIgnore, key, objects) {
    let id = parseInt('' + key, 10);
    if (isNaN(id)) {
      if (toIgnore.includes(key)) {
        return true;
      }
      let object = objects[key];
      if (object && object.id) {
        return toIgnore.find(ignore => { return ignore.id == object.id; });
      }
      else {
        return false;
      }
    }
    else {
      return toIgnore.find(ignore => { 
        return ignore.id && ignore.id == id;
      });
    }
  }

  objectHasExpired(object, options) {
    if (object
        && object._cachedAt
        && ((options.expiry_now - object._cachedAt) >= options.expiry)) {
      return true;
    }
    return false;
  }

  _markState(item, state) {

    if (item) {
      // Marked the passed in item and return it
      item._recordState = state;

      // Mark it in the store
      let itemInStore = StoreItemProcessing.findItem(this.itemType, item.id);

      if (itemInStore) {
        itemInStore._recordState = state;

        StoreItemProcessing.setItemInStore(this.itemType, itemInStore);
      }
      else {
        const normalizedItem = StoreItemProcessing.normalizeItem(item);
        StoreItemProcessing.setItemInStore(this.itemType, normalizedItem);
      }

    }
    return item;
  }

  markPending(item) {
    return this._markState(item, RecordStates.PENDING);
  }

  /** Interface for methods to be implemented by subclass */
  markNeedsSync(item) {
    return this._markState(item, RecordStates.NEEDS_SYNC);
  }

  markNeedsAttributeSync(item) {
    return this._markState(item, RecordStates.NEEDS_ATTRIBUTE_SYNC);
  }

  markSynced(item) {
    return this._markState(item, RecordStates.SYNCED);
  }

  markDeleted(item) {
    return this._markState(item, RecordStates.DELETED);
  }

  markError(item) {
    return this._markState(item, RecordStates.ERROR);
  }

  markDirty(item, dirtyState) {

    // todo: needs escalate up to the parent

    item._dirty = dirtyState;
    
    let itemInStore = StoreItemProcessing.findItem(this.itemType, item.id);

    if (itemInStore) {
      itemInStore._dirty = dirtyState;

      StoreItemProcessing.setItemInStore(this.itemType, itemInStore);
    }
    else {
      const normalizedItem = StoreItemProcessing.normalizeItem(item);
      StoreItemProcessing.setItemInStore(this.itemType, normalizedItem);
    }

    return item;
  }
  
  shouldRefresh(item, options) {
    if (!item || (options && options.force)) {
      return true;
    }
    else {
      if (item._recordState == RecordStates.NEEDS_ATTRIBUTE_SYNC || item._recordState == RecordStates.NEEDS_SYNC) {
        return true;
      }
      if (options && options.recordDescriptor) {
        let keys = Object.keys(options.recordDescriptor);
        let recordDescriptor = null;
        if (keys.length == 1) {
          recordDescriptor = options.recordDescriptor[keys[0]];
        }
        else {
          recordDescriptor = options.recordDescriptor;
        }
        if (!recordDescriptor) {
          if (item._recordState == RecordStates.NEEDS_ATTRIBUTE_SYNC || item._recordState == RecordStates.NEEDS_SYNC) {
            return true;
          }
        }
        else if (!StoreItemProcessing.isComplete(this.itemType, item.id, recordDescriptor, options ? options.checkArrays : false)) {
          return true;
        }
        else {
          return false;
        }
      }
    }
  }

  normalizeCollection(collection, options) {
    return StoreItemProcessing.normalizeCollection(this.itemType, collection, options);
  }

  denormalizeCollection(collection, recordDescriptor) {
    return StoreItemProcessing.denormalizeCollection(this.itemType, collection, recordDescriptor);
  }

  denormalizeItem(item, recordDescriptor) {
    return StoreItemProcessing.denormalizeItem(this.itemType, item, recordDescriptor);
  }

  //denormalizes item based on the record descriptor
  mapToReturn(item, options) {
    if (!options || !options.recordDescriptor) {
      return item;
    }
    else if (item){
      let keys = Object.keys(options.recordDescriptor);
      let recordDescriptor = null;
      if (keys.length == 1){
        recordDescriptor = options.recordDescriptor[keys[0]];
      }
      else{
        recordDescriptor = options.recordDescriptor;
      }
      const returnItem = this.denormalizeItem(item, recordDescriptor);
      return returnItem;
    }
    else{
      return null;
    }
  }

  async _getItem(itemId, serviceCall, options, overrideMapToReturn = null, preProcessIncludes, prefetchCallback, ignoreError) {
    let item = this.findItem(itemId);
    /*
      NOTE: the while below is present to account for this scenario:
        - a fetch for an item is triggered
        - before it can finish three more gets for the same item with
          the same record descriptor from different components fire all
          of which detect that the first fetch is in progress and all 3
          then are awaiting the initial fetch to finish... all good so far
        - the initial fetch finishes and stores the fetched item
        - without the wait, all three of the awaiting gets then continue
          and will trigger 3 gets (all of which may be the same)
      The while loop appears to prevent this since javascript is single
      threaded... so the first one that finishes its await and then triggers
      another fetch because its record descriptor is different from the 
      initial request will cause the other 2 to detect that a fetch is in 
      progress after awaiting the initial fetch
    */
    while (this.pendingRequests.has(itemId)) {
      if (prefetchCallback && item) {
        prefetchCallback(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
      }
      await this.pendingRequests.get(itemId);
    }
    if (itemId < 0) {
      if (item) {
        return Promise.resolve(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
      }
      else {
        return Promise.resolve(null);
      }
    }
    if (item && item._recordState == RecordStates.DELETED) {
      // for now return the old one
      return Promise.resolve(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
    }
    if (itemId > 0 && this.shouldRefresh(item, options)) {
      if (prefetchCallback && item) {
        prefetchCallback(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
      }
      if (dataModel.isOffline) {
        return new Promise(resolve => {
          if (item) {
            resolve(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
          }
          else {
            this.throwItemError();
          }
        });
      }
      
      const request = serviceCall();
      this.pendingRequests.set(itemId, request);
      let response = await request;
      this.pendingRequests.delete(itemId);

      if (response.offline) {
        if (!item) {
          this.throwItemError();
        }
        else {
          return overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options);
        }
      }
      else if (response.error) {
        if (!ignoreError) {
          dataModel.displayHTTPError('Getting a ' + this.itemType, response.error, false);
          if (!item) {
            this.throwItemError(this.itemType + ' non existent');
          }
          else {
            this.markError(item);
            this.throwItemError('Offline');
          }
        }
        else {
          this.markError(item);
        }
      }
      else {
        if (preProcessIncludes) {
          response = preProcessIncludes(response);
        }
        const storeItem = this.normalizeAndSyncToStore(response, { store: true });
        return Promise.resolve(overrideMapToReturn ? overrideMapToReturn(storeItem, options) : this.mapToReturn(storeItem, options));
      }

    }
    else {
      return Promise.resolve(overrideMapToReturn ? overrideMapToReturn(item, options) : this.mapToReturn(item, options));
    }
  }

  /**
   *  @param<string> message - response error from request
   *  @param<string> reason - a hardcoded constant (e.g. OFFLINE, HTTP)
   *  @param<object> item - the denormalized entity from store, may be null
  */
  throwItemError(message, item, reason) {
    // Object.assign is just to get around ESLint weirdness
    throw Object.assign({ message, item, reason });
  }
}



// WEBPACK FOOTER //
// ./src/dao/DAO.js